import { useSearchParams } from "react-router-dom";
import {
  Bot,
  Calendar,
  Mail,
  Megaphone,
  Network,
  Newspaper,
} from "lucide-react";
import { AgentChatConsole } from "@/components/agents/AgentChatConsole";
import { AgentContextPanel } from "@/components/agents/AgentContextPanel";

// ─── Agent registry ──────────────────────────────────────────────────────────

type AgentEntry = {
  name: string;
  label: string;
  icon: React.ReactNode;
  description: string;
};

const AGENTS: AgentEntry[] = [
  {
    name: "orchestrator",
    label: "Orchestrator",
    icon: <Network className="h-4 w-4" />,
    description: "Route tasks to the right specialist agent",
  },
  {
    name: "manager",
    label: "Manager",
    icon: <Bot className="h-4 w-4" />,
    description: "Goals, tour P&L, weekly digest",
  },
  {
    name: "booking",
    label: "Booking",
    icon: <Calendar className="h-4 w-4" />,
    description: "Venue pipeline, deal evaluation, tour clustering",
  },
  {
    name: "marketing",
    label: "Marketing",
    icon: <Megaphone className="h-4 w-4" />,
    description: "Release campaigns, list temperature, social",
  },
  {
    name: "publicist",
    label: "Publicist",
    icon: <Newspaper className="h-4 w-4" />,
    description: "Press tier ranking, pitch personalisation",
  },
  {
    name: "email",
    label: "Email",
    icon: <Mail className="h-4 w-4" />,
    description: "Outreach templates, follow-up audit",
  },
];

const DEFAULT_AGENT = "orchestrator";
const VALID_AGENT_NAMES = new Set(AGENTS.map((a) => a.name));

// ─── Hub page ─────────────────────────────────────────────────────────────────

export default function AgentsHubPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawAgent = searchParams.get("agent") ?? DEFAULT_AGENT;
  const activeAgent = VALID_AGENT_NAMES.has(rawAgent) ? rawAgent : DEFAULT_AGENT;

  function selectAgent(name: string) {
    setSearchParams({ agent: name }, { replace: true });
  }

  const entry = AGENTS.find((a) => a.name === activeAgent)!;

  return (
    <div className="flex flex-col gap-3 h-[calc(100vh-8rem)]">
      {/* ── Agent selector tab bar ──────────────────────────────────────── */}
      <div className="flex items-center gap-1 overflow-x-auto pb-1 shrink-0">
        {AGENTS.map((agent) => {
          const isActive = agent.name === activeAgent;
          return (
            <button
              key={agent.name}
              onClick={() => selectAgent(agent.name)}
              className={[
                "flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium whitespace-nowrap transition-colors",
                isActive
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground",
              ].join(" ")}
              title={agent.description}
            >
              {agent.icon}
              {agent.label}
            </button>
          );
        })}
      </div>

      {/* ── Content area: context sidebar + chat console ────────────────── */}
      {/*
        Key by activeAgent so both panels fully remount on agent switch —
        ensures each agent starts with a fresh chat thread and correct context.
      */}
      <div key={activeAgent} className="flex flex-1 min-h-0 gap-4">
        {/* Context sidebar — hidden on small screens */}
        <aside className="hidden w-72 shrink-0 overflow-y-auto lg:block">
          <AgentContextPanel agentName={activeAgent} />
        </aside>

        {/* Chat console */}
        <AgentChatConsole agentName={activeAgent} className="flex-1 min-h-0" />
      </div>
    </div>
  );
}
